import { MainPageContainer, UpperInfoContainer } from 'components/PageLayout';
import { convertToDate, useZurichCSIReport } from 'pages/hooks';
import { FormEvent, useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { dateToString, formatDate } from 'utils/helpers';
import { AxiosError } from 'axios';
import { createReportURL } from 'core/routes';
import { errorMessages } from 'api/resources/responseMessages/errorMessages';
import { ReportGridContainer } from 'components/ReportGridContainer';
import { NoReportDataMessage } from 'components/ReportsComponents/NoReportDataMessage';
import { ReportBackButton } from 'components/ReportsComponents/ReportBackButton';
import { ReportHeader } from 'components/ReportsComponents/ReportHeader';
import {
  columns,
  ZURICH_CSI_REPORT,
  ZURICH_CSI_REPORT_GRID_TITLE,
  ZURICH_CSI_REPORT_MESSAGE,
} from './constants';
import { ReportSearchForm } from 'components/ReportForms/ReportSearchForm';
import { ReportProps } from 'pages/ReportsDetails/types';
import { reportLogger } from 'pages/ReportsDetails/reportLogger';
import DevExtremeReportGrid from 'components/ReportGrid_New';
// import { mockedCSIReportData } from '../mocedReportData';


export function ZurichCSIReport({
  reportPath,
  setErrorMessage,
  setToast,
}: Readonly<ReportProps>) {
  const navigate = useNavigate();
  const location = useLocation();
  const urlParams = new URLSearchParams(location.search);
  const urlParamsValue = {
    startDate: urlParams.get('startDate'),
    endDate: urlParams.get('endDate'),
  };
  const [startDate, setStartDate] = useState<Date | null>(
    convertToDate(urlParamsValue.startDate) ?? null
  );
  const [endDate, setEndDate] = useState<Date | null>(
    convertToDate(urlParamsValue.endDate) ?? null
  );

  const queryEnabled = !!urlParamsValue.startDate && !!urlParamsValue.endDate;

  const { isReportFetching, refetchReport, reportData } = useZurichCSIReport(
    urlParamsValue.startDate,
    urlParamsValue.endDate,
    queryEnabled,
    handleError
  );

  const rows = useMemo(() => reportData, [reportData]);

  useEffect(() => {
    navigateIfPossible();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [urlParamsValue.startDate, urlParamsValue.endDate]);

  const reportHeaderTagLine = useMemo(
    () =>
      `CSI Triggers for delivered jobs between ${formatDate(startDate)} and ${formatDate(endDate)} on ${formatDate(new Date())}`,
    [startDate, endDate]
  );

  return (
    <MainPageContainer isLoading={isReportFetching} direction="column" wrap="nowrap">
      <UpperInfoContainer direction="column">
        <ReportBackButton />
        <ReportHeader
          reportName={ZURICH_CSI_REPORT}
          infoMessage={ZURICH_CSI_REPORT_MESSAGE}
        />
        <ReportSearchForm
          endDate={endDate}
          isDisabled={isReportFetching}
          startDate={startDate}
          setEndDate={setEndDate}
          setStartDate={setStartDate}
          handleSubmit={handleSubmit}
        />
      </UpperInfoContainer>

      {!isReportFetching && rows?.length === 0 && <NoReportDataMessage />}

      <ReportGridContainer direction="column">
        <DevExtremeReportGrid 
          columns={columns}
          displayGroupingPanel
          headerTitleForSheet={ZURICH_CSI_REPORT}
          isLoading={isReportFetching}
          pdfOrientation='l'
          reportHeaderTagLine={reportHeaderTagLine}
          rows={rows}
          title={ZURICH_CSI_REPORT_GRID_TITLE}
        />
      </ReportGridContainer>
    </MainPageContainer>
  );

  function handleSubmit(event: FormEvent) {
    event.preventDefault();
    if (
      urlParamsValue.startDate == dateToString(startDate) &&
      urlParamsValue.endDate == dateToString(endDate)
    )
      refetchReport();
    else navigateIfPossible();
  }

  function navigateIfPossible() {
    if (!!startDate && !!endDate)
      navigate(
        createReportURL(
          {
            startDate: dateToString(startDate),
            endDate: dateToString(endDate),
          },
          reportPath,
        ), { replace: true });
  }

  function handleError(error: AxiosError) {
    reportLogger(error);
    setErrorMessage(errorMessages.reports('Zurich CSI Report'));
    setToast(true);
  }
}
